import { useEffect, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useTour } from "./TourProvider";

const TIP_W = 320;
const TIP_H = 190;

/**
 * Guided-tour overlay (Scope A5): dims the page, spotlights the current step's target
 * (located by its `data-tour` attribute), and shows an advancing tooltip. Handles
 * cross-route navigation and per-step UI driving (e.g. flipping to Client view).
 *
 * The dim NEVER blocks clicks — the tour points at things, it doesn't lock the page.
 * Steps invite the visitor to try the spotlighted control ("go ahead and cancel one"),
 * and in a co-driven demo the live control bar + video must stay usable mid-tour. Only
 * the tooltip card takes pointer input. The dim sits below the live control bar (z-80)
 * and video (z-90) so neither is greyed out; the card sits above everything.
 *
 * In a co-driven demo the tour is shared (TourProvider syncs it), so both screens run
 * this component and either person can press Back / Next / Done.
 */
export function TourOverlay() {
  const { active, index, next, back, end } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState<DOMRect | null>(null);
  const step = active ? active.steps[index] : undefined;

  // Entering a step: go to its page and run its side-effect — ONCE per step, not on
  // every route change, so someone who clicks away mid-tour isn't yanked back. Both
  // screens run this in a co-driven demo, so step.run() must stay idempotent (it is:
  // "select the Client view tab"), and both navigations land on the same page.
  useEffect(() => {
    if (!step) return;
    const target = step.path;
    const needsNav = !!target && window.location.pathname !== target;
    if (needsNav && target) navigate(target);
    const t = setTimeout(() => step.run?.(), needsNav ? 280 : 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  // Spotlight: find the step's target on the current page, and keep tracking it.
  useEffect(() => {
    setRect(null);
    if (!step?.target) return; // no tour, or a centered card
    const selector = `[data-tour="${step.target}"]`;
    let cancelled = false;
    let tries = 0;
    const poll = () => {
      if (cancelled) return;
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        setTimeout(() => {
          if (!cancelled) setRect(el.getBoundingClientRect());
        }, 240);
        return;
      }
      if (tries++ < 35) setTimeout(poll, 80);
    };
    const t = setTimeout(poll, 120);
    const refresh = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("scroll", refresh, true);
    window.addEventListener("resize", refresh);
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener("scroll", refresh, true);
      window.removeEventListener("resize", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index, location.pathname]);

  if (!active || !step) return null;
  const last = index === active.steps.length - 1;

  let tipStyle: CSSProperties = { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
  if (rect) {
    const below = rect.bottom + 12 + TIP_H < window.innerHeight;
    const top = below ? rect.bottom + 12 : Math.max(12, rect.top - 12 - TIP_H);
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - TIP_W - 12);
    tipStyle = { left, top };
  }

  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-[75]" data-tour-dim>
        {rect ? (
          <div
            className="absolute rounded-lg ring-2 ring-[var(--color-gold)] transition-all duration-200"
            style={{ left: rect.left - 4, top: rect.top - 4, width: rect.width + 8, height: rect.height + 8, boxShadow: "0 0 0 9999px rgba(6,16,26,0.74)" }}
          />
        ) : (
          <div className="absolute inset-0 bg-[rgba(6,16,26,0.74)]" />
        )}
      </div>

      <div
        className="fixed z-[200] w-[320px] rounded-xl border border-[var(--color-border-gold)] bg-[var(--color-bg-deep)] p-4 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.8)]"
        style={tipStyle}
        data-tour-card
      >
        <button type="button" onClick={end} aria-label="End tour" className="absolute right-2.5 top-2.5 text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]">
          <X className="h-4 w-4" />
        </button>
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--color-gold-soft)]">
          {active.label} · {index + 1} / {active.steps.length}
        </p>
        <h3 className="mt-1.5 font-heading text-[18px] font-medium text-[var(--color-text-primary)]">{step.title}</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--color-text-muted)]">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={index === 0}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)] disabled:opacity-30"
          >
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-gold)] px-4 py-2 font-mono text-[10px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
          >
            {last ? (<><Check className="h-3.5 w-3.5" /> Done</>) : (<>Next <ArrowRight className="h-3.5 w-3.5" /></>)}
          </button>
        </div>
      </div>
    </>
  );
}
